package com.schoolconnect.app.feature.eventsnews

import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.datasource.network.response.ParentEventListItemResponse
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.parentevents.ParentEventsRepository
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.serialization.json.*
import kotlin.time.Clock

class ParentEventsNewsViewModel(initialChildren: List<ParentAttendanceChild> = emptyList()) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _state = MutableStateFlow(ParentEventsNewsState(children = initialChildren))
    val state: StateFlow<ParentEventsNewsState> = _state.asStateFlow()
    private var listJob: Job? = null
    init {
        observeGlobalSelection()
        loadPage()
    }

    private fun observeGlobalSelection() {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _state.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _state.value.selectedChildIndex) onEvent(ParentEventsNewsEvent.ChildSelected(index))
            }
        }
    }

    fun onEvent(event: ParentEventsNewsEvent) {
        when (event) {
            is ParentEventsNewsEvent.ChildSelected -> { _state.update { it.copy(selectedChildIndex = event.index.coerceIn(0, it.children.lastIndex.coerceAtLeast(0)), pageIndex = 0) }; loadPage() }
            is ParentEventsNewsEvent.SearchChanged -> { _state.update { it.copy(search = event.value, pageIndex = 0) }; loadPage(debounce = true) }
            is ParentEventsNewsEvent.PeriodChanged -> { _state.update { it.copy(period = event.value, periodFrom = if (event.value == "Custom Range") it.periodFrom else "", periodTo = if (event.value == "Custom Range") it.periodTo else "", pageIndex = 0) }; loadPage() }
            is ParentEventsNewsEvent.CustomPeriodChanged -> { _state.update { it.copy(period = "Custom Range", periodFrom = event.from, periodTo = event.to, pageIndex = 0) }; loadPage() }
            is ParentEventsNewsEvent.StatusChanged -> { _state.update { it.copy(status = event.value, pageIndex = 0) }; loadPage() }
            ParentEventsNewsEvent.FiltersCleared -> { _state.update { it.copy(search = "", period = "All", periodFrom = "", periodTo = "", status = "All", pageIndex = 0) }; loadPage() }
            is ParentEventsNewsEvent.PageChanged -> { _state.update { it.copy(pageIndex = event.index.coerceAtLeast(0)) }; loadPage() }
            is ParentEventsNewsEvent.PageSizeChanged -> { _state.update { it.copy(pageSize = event.size.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }; loadPage() }
            is ParentEventsNewsEvent.SortChanged -> { _state.update { current -> current.copy(sortField = event.field, sortAscending = if (current.sortField == event.field) !current.sortAscending else true, pageIndex = 0) }; loadPage() }
            is ParentEventsNewsEvent.ViewRequested -> loadDetail(event.row)
            ParentEventsNewsEvent.ViewDismissed -> _state.update { it.copy(viewing = null, isViewingLoading = false, viewingError = "") }
        }
    }

    private fun loadPage(debounce: Boolean = false) {
        listJob?.cancel()
        listJob = scope.launch {
            if (debounce) delay(300)
            val requested = _state.value
            if (_state.value.children.isEmpty()) {
                _state.update { it.copy(rows = emptyList(), totalCount = 0, isLoading = false, errorMessage = "No linked child was found.") }
                return@launch
            }
            _state.update { it.copy(isLoading = true, errorMessage = "") }
            // The parent events endpoint derives the authorized children from the
            // authenticated parent session. Supplying a child id here causes the
            // backend to reject the request for this parent, even though the id
            // comes from the parent's dashboard response.
            val (publishedFrom, publishedTo) = requested.toPublishedRange()
            ParentEventsRepository.loadPage(
                query = requested.search.trim().takeIf { it.isNotBlank() },
                status = requested.status.toApiParentEventStatus(),
                publishedFrom = publishedFrom,
                publishedTo = publishedTo,
                pageIndex = requested.pageIndex,
                pageSize = requested.pageSize,
                sortBy = requested.sortField.toParentEventSortField(),
                order = if (requested.sortAscending) "asc" else "desc"
            ).onSuccess { result ->
                val lastPageIndex = (pageCountForTotal(result.totalCount, requested.pageSize) - 1).coerceAtLeast(0)
                if (requested.pageIndex > lastPageIndex) {
                    _state.update { it.copy(pageIndex = lastPageIndex) }
                    listJob = null
                    loadPage()
                    return@launch
                }
                _state.update { current ->
                    current.copy(
                        rows = result.items.map(ParentEventListItemResponse::toRow),
                        totalCount = result.totalCount,
                        isLoading = false,
                        errorMessage = ""
                    )
                }
            }.onFailure { error ->
                _state.update { it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load events.") }
            }
        }
    }

    private fun loadDetail(row: EventRowUi) {
        _state.update { it.copy(viewing = row, isViewingLoading = true, viewingError = "") }
        scope.launch {
            ParentEventsRepository.loadDetail(row.backendId.ifBlank { row.id }).onSuccess { raw ->
                val parsed = raw.toDetailRow(row)
                val attachments = loadAttachmentDetails(parsed.attachmentFileIds, parsed.attachmentFileUrls)
                _state.update { it.copy(viewing = parsed.copy(attachments = attachments), isViewingLoading = false) }
            }.onFailure { error -> _state.update { it.copy(isViewingLoading = false, viewingError = error.message ?: "Unable to load event details.") } }
        }
    }

    fun clear() { listJob?.cancel(); scope.cancel() }
}

private fun ParentEventsNewsState.toPublishedRange(): Pair<String?, String?> {
    val from = appDateInputToApi(periodFrom)
    val to = appDateInputToApi(periodTo)
    if (period.equals("Custom Range", ignoreCase = true)) return from to to
    val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    return when (period.trim().lowercase()) {
        "today" -> today.toString() to today.toString()
        "last 7 days" -> today.minus(6, DateTimeUnit.DAY).toString() to today.toString()
        "this month" -> kotlinx.datetime.LocalDate(today.year, today.month, 1).toString() to today.toString()
        else -> null to null
    }
}

private fun String.toApiParentEventStatus(): String? = when (trim().lowercase()) {
    "active" -> "ACTIVE"
    "expired" -> "EXPIRED"
    else -> null
}

private fun EventsSortField.toParentEventSortField(): String = when (this) {
    EventsSortField.ID -> "event_id"
    EventsSortField.TITLE -> "title"
    EventsSortField.AUDIENCE -> "audience"
    EventsSortField.PUBLISHED_DATE -> "created_at"
    EventsSortField.EXPIRY_DATE -> "event_date"
    EventsSortField.STATUS -> "status"
}

private fun ParentEventListItemResponse.toRow(): EventRowUi = EventRowUi(
    id = eventId.orEmpty().ifBlank { id.orEmpty() },
    backendId = id.orEmpty().ifBlank { eventId.orEmpty() },
    title = title.orEmpty().ifBlank { "—" },
    eventType = "Event",
    publishedOn = publishedOn.orEmpty().toDisplayDate(),
    expiryDate = expiryDate.orEmpty().toDisplayDate(),
    status = status.orEmpty().ifBlank { "Active" }.toDisplayStatus(),
    description = description.orEmpty(),
    audience = applicableTo.orEmpty().ifBlank { "—" }
)
private fun JsonObject.toDetailRow(fallback: EventRowUi): EventRowUi = EventRowUi(
    id = firstString("event_id") ?: fallback.id,
    backendId = firstString("id") ?: fallback.backendId,
    title = firstString("title", "name") ?: fallback.title,
    eventType = firstString("event_type", "type") ?: fallback.eventType,
    publishedOn = (firstString("published_on", "published_at", "created_at") ?: fallback.publishedOn).toDisplayDate(),
    expiryDate = (firstString("expiry_date", "expires_at", "event_date") ?: fallback.expiryDate).toDisplayDate(),
    status = (firstString("status") ?: fallback.status).toDisplayStatus(),
    description = firstString("description", "content", "body") ?: fallback.description,
    audience = firstString("applicable_to", "audience") ?: fallback.audience,
    attachmentFileIds = firstStringList("attachment_file_ids", "file_ids") ?: fallback.attachmentFileIds,
    attachmentFileUrls = firstStringList("attachment_file_urls", "file_urls") ?: fallback.attachmentFileUrls
)
private fun JsonObject.firstString(vararg keys: String): String? = keys.firstNotNullOfOrNull { get(it)?.jsonPrimitive?.contentOrNull?.takeIf(String::isNotBlank) }
private fun JsonObject.firstStringList(vararg keys: String): List<String>? = keys.firstNotNullOfOrNull { key -> get(key)?.jsonArray?.mapNotNull { it.jsonPrimitive.contentOrNull?.takeIf(String::isNotBlank) } }
private suspend fun loadAttachmentDetails(fileIds: List<String>, fileUrls: List<String>): List<EventAttachmentDetailUi> = (0 until maxOf(fileIds.size, fileUrls.size)).map { index ->
    val fileId = fileIds.getOrNull(index).orEmpty()
    val suppliedUrl = fileUrls.getOrNull(index).orEmpty()
    val metadata = fileId.takeIf(String::isNotBlank)?.let { FileUploadRepository.getMetadata(it).getOrNull() }
    val url = suppliedUrl.ifBlank { fileId.takeIf(String::isNotBlank)?.let { FileUploadRepository.getDownloadUrl(it).getOrNull()?.url }.orEmpty() }
    val mimeType = metadata?.detectedMimeType ?: metadata?.declaredMimeType ?: mimeTypeFromName(url)
    EventAttachmentDetailUi(
        fileId = fileId,
        name = metadata?.originalFilename.orEmpty().ifBlank { fileNameFromUrl(url).ifBlank { "Attachment ${index + 1}" } },
        mimeType = mimeType,
        sizeLabel = metadata?.sizeBytes?.let(::formatParentEventFileSize) ?: "File",
        url = url,
        previewBytes = if (mimeType.startsWith("image/", ignoreCase = true) && url.isNotBlank()) FileUploadRepository.downloadBytes(url).getOrNull() else null
    )
}
private fun fileNameFromUrl(url: String): String = url.substringBefore('?').substringAfterLast('/').trim().ifBlank { "" }
private fun mimeTypeFromName(url: String): String = when (fileNameFromUrl(url).substringAfterLast('.', "").lowercase()) {
    "jpg", "jpeg" -> "image/jpeg"
    "png" -> "image/png"
    "gif" -> "image/gif"
    "webp" -> "image/webp"
    "mp4", "mov", "webm" -> "video/mp4"
    "pdf" -> "application/pdf"
    else -> "application/octet-stream"
}
private fun formatParentEventFileSize(bytes: Long): String = when {
    bytes >= 1024L * 1024L -> "${(bytes / (1024.0 * 1024.0) * 10).toLong() / 10.0} MB"
    bytes >= 1024L -> "${(bytes / 1024.0 * 10).toLong() / 10.0} KB"
    else -> "$bytes B"
}
private fun JsonObject.firstArray(vararg keys: String): JsonArray = keys.firstNotNullOfOrNull { get(it)?.jsonArray } ?: JsonArray(emptyList())
private fun String.toDisplayDate(): String = take(10).split("-").let { p -> if (p.size == 3) "${p[2]} ${listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[p[1].toIntOrNull()?.minus(1) ?: 0]} ${p[0]}" else this }
private fun String.toDisplayStatus(): String = when (uppercase().replace('_', ' ')) { "EXPIRED" -> "Expired"; "ACTIVE", "PUBLISHED" -> "Active"; else -> replaceFirstChar { it.uppercase() } }
